import { useAuth, ADMIN_ROLE_NAMES } from '@kss/auth';

/**
 * Resolved permission flags for a single module ("Product", "SalesOrder",
 * etc.). Derived from the JWT `perm[]` claim that ships with every login
 * response — see `extractPermissions` in `@kss/auth`.
 *
 * The backend currently seeds five canonical actions per module:
 *
 *   {Module}.List     — read the list grid
 *   {Module}.Read     — read a single record
 *   {Module}.Create   — POST new records
 *   {Module}.Update   — PUT existing records
 *   {Module}.Delete   — POST /{Module}/Delete
 *
 * Three product-side actions are NOT in the backend seed yet:
 *
 *   {Module}.Import          — bulk import CSV / template upload
 *   {Module}.Export          — download all rows as CSV
 *   {Module}.DownloadTemplate — download a header-only CSV
 *
 * Until the backend ships them, this hook FALLS BACK to "allowed when the
 * user has at least the .List permission" — i.e. anyone who can see the
 * grid can also export from it. Once the backend seeds the new permissions
 * (see `BACKEND_PROMPT_NEW_PERMS.md`), the fallback is harmlessly skipped
 * because the explicit permission takes precedence.
 */
export interface ModulePerms {
  module: string;
  canList: boolean;
  canRead: boolean;
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  canImport: boolean;
  canExport: boolean;
  canDownloadTemplate: boolean;
  /** Convenience union: true when the user can mutate (create/update/delete). */
  canMutate: boolean;
  /** True when the JWT carried any "{module}.*" permission. */
  hasAny: boolean;
}

const ALWAYS_TRUE: ModulePerms = {
  module: '*',
  canList: true,
  canRead: true,
  canCreate: true,
  canUpdate: true,
  canDelete: true,
  canImport: true,
  canExport: true,
  canDownloadTemplate: true,
  canMutate: true,
  hasAny: true,
};

function lc(p: string): string {
  return String(p).trim().toLowerCase();
}

/**
 * Returns boolean flags for the canonical CRUD + import/export actions on
 * the given backend module. Read once near the top of every list/edit page
 * and feed the booleans into the toolbar / action buttons:
 *
 *   const p = useModulePerms('Product');
 *   {p.canCreate && <Button onClick={...}>New</Button>}
 *   {p.canDelete && <button title="Delete" ...>}
 */
export function useModulePerms(module: string): ModulePerms {
  const { user } = useAuth();

  // Admin bypass — EXACT-MATCH against the seeded admin-flavour role
  // allowlist (Administrator / SuperAdmin / TenantAdmin / Owner) shared
  // with `useHasRole` + `useHasPermission` in @kss/auth. Substring
  // matches (`role.includes('admin')`) are deliberately avoided so a
  // future custom role like `NonAdmin` or `AdminAssistant` doesn't
  // silently gain full-module access.
  const role = lc(user?.role ?? '');
  if (ADMIN_ROLE_NAMES.has(role)) {
    return { ...ALWAYS_TRUE, module };
  }

  const granted = new Set((user?.permissions ?? []).map(lc));
  const has = (action: string) => granted.has(lc(`${module}.${action}`));

  const canList = has('List');
  const canRead = has('Read');
  const canCreate = has('Create');
  const canUpdate = has('Update');
  const canDelete = has('Delete');

  // Backend doesn't seed Import / Export / DownloadTemplate yet — fall
  // back to "any list-viewer can also export". When the backend adds
  // explicit perms, the explicit grant wins (true || * = true).
  const canImport = has('Import') || canList;
  const canExport = has('Export') || canList;
  const canDownloadTemplate = has('DownloadTemplate') || canList;

  return {
    module,
    canList,
    canRead,
    canCreate,
    canUpdate,
    canDelete,
    canImport,
    canExport,
    canDownloadTemplate,
    canMutate: canCreate || canUpdate || canDelete,
    hasAny: canList || canRead || canCreate || canUpdate || canDelete,
  };
}
